/**
 * NotBuiltYet.jsx — an honest placeholder.
 *
 * Nine pages are routed but not yet written. Without something here, Vite fails
 * to resolve the import and the whole app refuses to start — so a missing
 * Settings page would stop you signing in.
 *
 * These placeholders say plainly what the screen will do and what already
 * exists behind it. That matters: the failure mode I am avoiding is Aarman
 * clicking a nav item, seeing something blank or broken, and spending an hour
 * assuming he has misconfigured Firebase.
 */

import { Link } from 'react-router-dom';
import { Hammer, ArrowLeft } from 'lucide-react';

export default function NotBuiltYet({ title, purpose, ready = [], route }) {
  return (
    <div className="container page" style={{ paddingTop: 28 }}>
      <div className="card nbuilt">
        <Hammer size={26} aria-hidden="true" />

        <h1>{title}</h1>
        <p className="nbuilt__purpose">{purpose}</p>

        {ready.length > 0 && (
          <div className="nbuilt__ready">
            <span className="eyebrow">Already written, waiting to be wired up</span>
            <ul>
              {ready.map((item) => <li key={item}><code>{item}</code></li>)}
            </ul>
          </div>
        )}

        <p className="nbuilt__note">
          This is a placeholder, not an error. Nothing is broken and nothing you
          did caused it — the page simply has not been built yet.
          {route && <> Route: <code>{route}</code></>}
        </p>

        <div className="nbuilt__actions">
          {/*
            The arrow is deliberately sized to match the text rather than being
            a small decorative mark. `flex-shrink: 0` and the shared gap in .btn
            keep it on the same line and optically centred with the label — it
            previously read as a stray glyph rather than part of the button.
          */}
          <Link to="/" className="btn btn--primary nbuilt__back">
            <ArrowLeft size={18} strokeWidth={2.5} aria-hidden="true" />
            <span>Back to your briefing</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

export const NOT_BUILT_CSS = `
.nbuilt { padding: 32px 26px; text-align: center; }
.nbuilt svg { color: var(--ink-faint); margin-bottom: 14px; }
.nbuilt h1 { font-family: var(--font-display); font-size: var(--text-xl); margin: 0 0 8px; }
.nbuilt__purpose {
  color: var(--ink-soft); font-size: var(--text-sm); line-height: 1.65;
  margin: 0 auto 20px; max-width: 46ch;
}
.nbuilt__ready {
  text-align: left; display: inline-block;
  padding: 14px 18px; margin-bottom: 20px;
  background: var(--bg-alt); border-radius: var(--radius);
  border: 1px solid var(--line-strong);
}
.nbuilt__ready ul { margin: 8px 0 0; padding-left: 18px; }
.nbuilt__ready li { font-size: var(--text-sm); color: var(--ink-soft); line-height: 1.8; }
.nbuilt code {
  font-family: var(--font-mono); font-size: 0.9em;
  background: var(--card); padding: 2px 6px; border-radius: 4px;
}
.nbuilt__note {
  font-size: var(--text-xs); color: var(--ink-faint);
  line-height: 1.6; margin: 0 auto 22px; max-width: 44ch;
}
.nbuilt__actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }

/* Belt and braces on top of the global .btn > svg rule, because this is the
   button Aarman kept seeing render wrong. */
.nbuilt__back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  line-height: 1;
  padding: 12px 22px;
}
.nbuilt__back svg { display: block; flex: 0 0 auto; }
.nbuilt__back span { display: block; line-height: 1; }
`;
